/**
 * Which parts of the app's own chrome a page of the new design has taken over (PV1b, the `shouldShowHeader` seam).
 *
 * A pavilion page brings its own header, so today's global <Header /> stands down on a route whose door is loading or
 * showing the new design, and comes back the moment the door falls back to today's page. The door claims the part in
 * a LAYOUT effect, so the claim is in place before the first paint; App.tsx reads it through a leaf subscriber, so a
 * claim re-renders only that leaf. Counted, so two doors (StrictMode's double effect included) never release each
 * other's claim; a release is idempotent. On the first download, and imports nothing.
 *
 * Today only `/` has a door, and `/` already hides the global header (App.tsx `isLandingPage`), so nothing visible
 * changes; the seam is for the pages that follow (design-system §7b).
 */
import { useSyncExternalStore } from 'react';

export type AppChromePart = 'header';

const claims: Record<AppChromePart, number> = { header: 0 };
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of [...listeners]) l();
}

/** Claim a part; returns its release, which may be called any number of times. */
export function claimAppChrome(part: AppChromePart): () => void {
  claims[part] += 1;
  emit();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    claims[part] = Math.max(0, claims[part] - 1);
    emit();
  };
}

export function isAppChromeClaimed(part: AppChromePart): boolean {
  return claims[part] > 0;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Whether a page of the new design has taken over this part of the app's chrome. */
export function useAppChromeClaimed(part: AppChromePart): boolean {
  return useSyncExternalStore(subscribe, () => isAppChromeClaimed(part), () => false);
}
